import {
	ContentCopyOutlined,
	DeleteOutlineOutlined,
	Download,
	FileCopyOutlined,
	LinkOutlined,
	PublishedWithChanges,
	SortOutlined,
} from "@mui/icons-material";
import EditIcon from "@mui/icons-material/Edit";
import DriveFileRenameOutlineIcon from "@mui/icons-material/DriveFileRenameOutline";
import VisibilityIcon from "@mui/icons-material/Visibility";
import WhatsAppIcon from "@mui/icons-material/WhatsApp";
import {
	Box,
	CircularProgress,
	Stack,
	Tooltip,
	Typography,
} from "@mui/material";
import React from "react";
import { ContextMenu } from "../ContextMenu/ContextMenu";
import { IContextMenuProps, IMenuOption } from "../ContextMenu/interfaces/IContextMenuProps";

import CommentIcon from "@mui/icons-material/Comment";
import HistoryIcon from "@mui/icons-material/History";
import { Dialog } from "../Dialogs/Dialog";
import { TableCellWidthContext } from "../TableV2/components/RenderTableV2/RenderTableV2";

export type { IMenuOption, IContextMenuProps };

interface IStandardTableActionsProps {
	status?: string;
	onDeleteClick?: () => void;
	onReactiveClick?: () => void;
	commentBoxTypeId?: string | number;
	onViewClick?: () => void;
	onQuickEditClick?: () => void;
	onEditClick?: () => void;
	onLinkDirect?: () => void;

	disableEdit?: boolean;
	onViewHistoryClick?: () => void;
	onCommentClick?: () => void;
	onDownloadPreview?: () => void;
	onDownLoadClick?: () => void;
	onDuplicateClick?: () => void;
	onViewDetails?: () => void;
	onWhatsappClick?: () => void;
	onWhatsappNewMessageClick?: () => void;
	downloadLoading?: boolean;
	more?: {
		menuItems: IContextMenuProps["menuOptions"];
	};
}

export const StandardTableActions: React.FC<IStandardTableActionsProps> = (
	props
) => {
	const {
		status,
		onDeleteClick,
		onReactiveClick,
		onViewClick,
		onQuickEditClick,
		onEditClick,
		onLinkDirect,

		onViewHistoryClick,
		onCommentClick,
		onDownLoadClick,
		onDownloadPreview,
		onDuplicateClick,
		onViewDetails,
		onWhatsappClick,
		onWhatsappNewMessageClick,
		downloadLoading,
		more,
		disableEdit,
	} = props;

	const [openDeleteModal, setOpenDeleteModal] = React.useState(false);

	const cellContext = React.useContext(TableCellWidthContext);
	const containerRef = React.useRef<HTMLDivElement>(null);
	const [measuredWidth, setMeasuredWidth] = React.useState<number | null>(null);

	React.useLayoutEffect(() => {
		if (cellContext?.width !== undefined) {
			return;
		}

		const element = containerRef.current;
		if (!element) return;

		const updateWidth = () => {
			const cell = (element.closest('[data-table-v2-cell="true"]') ||
				element.closest("td") ||
				element.parentElement) as HTMLElement | null;

			const target = cell || element;
			const style = window.getComputedStyle(target);
			const pl = parseFloat(style.paddingLeft) || 0;
			const pr = parseFloat(style.paddingRight) || 0;
			const available = target.getBoundingClientRect().width - pl - pr;
			setMeasuredWidth(Math.max(0, available));
		};

		updateWidth();

		const cell = (element.closest('[data-table-v2-cell="true"]') ||
			element.closest("td") ||
			element.parentElement) as HTMLElement | null;

		const observer = new ResizeObserver(() => {
			updateWidth();
		});

		if (cell) {
			observer.observe(cell);
		} else {
			observer.observe(element);
		}

		return () => {
			observer.disconnect();
		};
	}, [cellContext?.width]);

	const availableWidth =
		cellContext?.width !== undefined
			? Math.max(0, cellContext.width - (cellContext.paddingLeft || 0))
			: measuredWidth !== null
			? measuredWidth
			: undefined;

	const actions: Array<{
		id: string;
		label: string;
		icon: React.ReactNode;
		disabled?: boolean;
		onClick?: () => void;
		render: React.ReactNode;
	}> = [];

	if (onDeleteClick && status !== "INACTIVE" && status !== "ARCHIVE") {
		actions.push({
			id: "delete",
			label: "Delete",
			icon: <DeleteOutlineOutlined fontSize="small" color="error" />,
			onClick: () => setOpenDeleteModal(true),
			render: (
				<Box
					key="delete"
					onClick={(e) => {
						e.stopPropagation();
						setOpenDeleteModal(true);
					}}
					sx={{
						display: "inline-flex",
						alignItems: "center",
						justifyContent: "center",
						width: 24,
						height: 24,
						flexShrink: 0,
						cursor: "pointer",
					}}
				>
					<Tooltip title="Delete">
						<DeleteOutlineOutlined
							sx={{ cursor: "pointer" }}
							fontSize="small"
							color="error"
						/>
					</Tooltip>
				</Box>
			),
		});
	}

	if (onReactiveClick && status === "INACTIVE") {
		actions.push({
			id: "reactive",
			label: "Reactive",
			icon: <PublishedWithChanges fontSize="small" color="error" />,
			onClick: () => setOpenDeleteModal(true),
			render: (
				<Box
					key="reactive"
					onClick={(e) => {
						e.stopPropagation();
						setOpenDeleteModal(true);
					}}
					sx={{
						display: "inline-flex",
						alignItems: "center",
						justifyContent: "center",
						width: 24,
						height: 24,
						flexShrink: 0,
						cursor: "pointer",
					}}
				>
					<Tooltip title="Reactive">
						<PublishedWithChanges
							sx={{ cursor: "pointer" }}
							fontSize="small"
							color="error"
						/>
					</Tooltip>
				</Box>
			),
		});
	}

	if (onViewClick) {
		actions.push({
			id: "view",
			label: "View",
			icon: <VisibilityIcon color="primary" />,
			onClick: onViewClick,
			render: (
				<Box
					key="view"
					onClick={(e) => {
						e.stopPropagation();
						onViewClick();
					}}
					sx={{
						display: "inline-flex",
						alignItems: "center",
						justifyContent: "center",
						width: 24,
						height: 24,
						flexShrink: 0,
						cursor: "pointer",
					}}
				>
					<Tooltip title="View">
						<VisibilityIcon color="primary" />
					</Tooltip>
				</Box>
			),
		});
	}

	if (onQuickEditClick) {
		actions.push({
			id: "quickEdit",
			label: "Quick Edit",
			disabled: disableEdit,
			icon: (
				<DriveFileRenameOutlineIcon
					color={disableEdit ? "disabled" : "primary"}
				/>
			),
			onClick: () => (disableEdit ? null : onQuickEditClick()),
			render: (
				<Box
					key="quickEdit"
					onClick={(e) => {
						e.stopPropagation();
						if (!disableEdit) onQuickEditClick();
					}}
					sx={{
						display: "inline-flex",
						alignItems: "center",
						justifyContent: "center",
						width: 24,
						height: 24,
						flexShrink: 0,
						cursor: disableEdit ? "default" : "pointer",
					}}
				>
					<Tooltip title="Quick Edit">
						<DriveFileRenameOutlineIcon
							color={disableEdit ? "disabled" : "primary"}
						/>
					</Tooltip>
				</Box>
			),
		});
	}

	if (onEditClick) {
		actions.push({
			id: "edit",
			label: "Edit",
			disabled: disableEdit,
			icon: <EditIcon color={disableEdit ? "disabled" : "primary"} />,
			onClick: () => (disableEdit ? null : onEditClick()),
			render: (
				<Box
					key="edit"
					onClick={(e) => {
						e.stopPropagation();
						if (!disableEdit) onEditClick();
					}}
					sx={{
						display: "inline-flex",
						alignItems: "center",
						justifyContent: "center",
						width: 24,
						height: 24,
						flexShrink: 0,
						cursor: disableEdit ? "default" : "pointer",
					}}
				>
					<Tooltip title="Edit">
						<EditIcon color={disableEdit ? "disabled" : "primary"} />
					</Tooltip>
				</Box>
			),
		});
	}

	if (onDuplicateClick) {
		actions.push({
			id: "duplicate",
			label: "Duplicate",
			icon: <ContentCopyOutlined color="primary" />,
			onClick: onDuplicateClick,
			render: (
				<Box
					key="duplicate"
					onClick={(e) => {
						e.stopPropagation();
						onDuplicateClick();
					}}
					sx={{
						display: "inline-flex",
						alignItems: "center",
						justifyContent: "center",
						width: 24,
						height: 24,
						flexShrink: 0,
						cursor: "pointer",
					}}
				>
					<Tooltip title="Duplicate">
						<ContentCopyOutlined color="primary" />
					</Tooltip>
				</Box>
			),
		});
	}

	if (onViewHistoryClick) {
		actions.push({
			id: "history",
			label: "History",
			icon: <HistoryIcon color="primary" />,
			onClick: onViewHistoryClick,
			render: (
				<Box
					key="history"
					onClick={(e) => {
						e.stopPropagation();
						onViewHistoryClick();
					}}
					sx={{
						display: "inline-flex",
						alignItems: "center",
						justifyContent: "center",
						width: 24,
						height: 24,
						flexShrink: 0,
						cursor: "pointer",
					}}
				>
					<Tooltip title="History">
						<HistoryIcon color="primary" />
					</Tooltip>
				</Box>
			),
		});
	}

	if (onCommentClick) {
		actions.push({
			id: "comment",
			label: "Activity",
			icon: <CommentIcon color="primary" />,
			onClick: onCommentClick,
			render: (
				<Box
					key="comment"
					onClick={(e) => {
						e.stopPropagation();
						onCommentClick();
					}}
					sx={{
						display: "inline-flex",
						alignItems: "center",
						justifyContent: "center",
						width: 24,
						height: 24,
						flexShrink: 0,
						cursor: "pointer",
					}}
				>
					<Tooltip title="Activity">
						<CommentIcon color="primary" />
					</Tooltip>
				</Box>
			),
		});
	}

	if (onDownloadPreview) {
		actions.push({
			id: "downloadPreview",
			label: "Preview",
			icon: <FileCopyOutlined color="primary" />,
			onClick: onDownloadPreview,
			render: (
				<Box
					key="downloadPreview"
					onClick={(e) => {
						e.stopPropagation();
						onDownloadPreview();
					}}
					sx={{
						display: "inline-flex",
						alignItems: "center",
						justifyContent: "center",
						width: 24,
						height: 24,
						flexShrink: 0,
						cursor: "pointer",
					}}
				>
					<Tooltip title="Preview">
						<FileCopyOutlined color="primary" />
					</Tooltip>
				</Box>
			),
		});
	}

	if (onViewDetails) {
		actions.push({
			id: "viewDetails",
			label: "View Details",
			icon: <SortOutlined color="primary" />,
			onClick: onViewDetails,
			render: (
				<Box
					key="viewDetails"
					onClick={(e) => {
						e.stopPropagation();
						onViewDetails();
					}}
					sx={{
						display: "inline-flex",
						alignItems: "center",
						justifyContent: "center",
						width: 24,
						height: 24,
						flexShrink: 0,
						cursor: "pointer",
					}}
				>
					<Tooltip title="View Details">
						<SortOutlined color="primary" />
					</Tooltip>
				</Box>
			),
		});
	}

	if (onLinkDirect) {
		actions.push({
			id: "linkDirect",
			label: "Open Prospect Link",
			icon: <LinkOutlined color="primary" />,
			onClick: onLinkDirect,
			render: (
				<Box
					key="linkDirect"
					onClick={(e) => {
						e.stopPropagation();
						onLinkDirect();
					}}
					sx={{
						display: "inline-flex",
						alignItems: "center",
						justifyContent: "center",
						width: 24,
						height: 24,
						flexShrink: 0,
						cursor: "pointer",
					}}
				>
					<Tooltip title="Open Prospect Link">
						<LinkOutlined color="primary" />
					</Tooltip>
				</Box>
			),
		});
	}

	if (onDownLoadClick) {
		actions.push({
			id: "download",
			label: "Download",
			disabled: downloadLoading,
			icon: <Download color="primary" />,
			onClick: !downloadLoading ? onDownLoadClick : undefined,
			render: (
				<Box
					key="download"
					onClick={(e) => {
						e.stopPropagation();
						if (!downloadLoading && onDownLoadClick) onDownLoadClick();
					}}
					sx={{
						display: "inline-flex",
						alignItems: "center",
						justifyContent: "center",
						width: 24,
						height: 24,
						flexShrink: 0,
						cursor: downloadLoading ? "default" : "pointer",
					}}
				>
					<Tooltip title="Download">
						<Download color="primary" />
					</Tooltip>
				</Box>
			),
		});
	}

	if (onWhatsappClick) {
		actions.push({
			id: "whatsapp",
			label: "WhatsApp",
			disabled: disableEdit,
			icon: <WhatsAppIcon />,
			onClick: () => (disableEdit ? null : onWhatsappClick()),
			render: (
				<Box
					key="whatsapp"
					onClick={(e) => {
						e.stopPropagation();
						if (!disableEdit && onWhatsappClick) onWhatsappClick();
					}}
					sx={{
						display: "inline-flex",
						alignItems: "center",
						justifyContent: "center",
						width: 24,
						height: 24,
						flexShrink: 0,
						cursor: disableEdit ? "default" : "pointer",
					}}
				>
					<Tooltip title="WhatsApp">
						<WhatsAppIcon />
					</Tooltip>
				</Box>
			),
		});
	}

	if (onWhatsappNewMessageClick) {
		actions.push({
			id: "whatsappNewMessage",
			label: "New Message",
			disabled: disableEdit,
			icon: <WhatsAppIcon color="success" />,
			onClick: () => (disableEdit ? null : onWhatsappNewMessageClick()),
			render: (
				<Box
					key="whatsappNewMessage"
					onClick={(e) => {
						e.stopPropagation();
						if (!disableEdit && onWhatsappNewMessageClick)
							onWhatsappNewMessageClick();
					}}
					sx={{
						display: "inline-flex",
						alignItems: "center",
						justifyContent: "center",
						width: 24,
						height: 24,
						flexShrink: 0,
						cursor: disableEdit ? "default" : "pointer",
					}}
				>
					<Tooltip title="New Message">
						<WhatsAppIcon color="success" />
					</Tooltip>
				</Box>
			),
		});
	}

	const ITEM_WIDTH = 24;
	const GAP = 8;
	const totalActions = actions.length;
	const hasOriginalMore = Boolean(
		more && more.menuItems && more.menuItems.length > 0
	);
	const originalMenuItems = more?.menuItems || [];

	let visibleCount = totalActions;
	let showMenu = hasOriginalMore;

	if (availableWidth !== undefined && totalActions > 0) {
		const widthForAllWithoutMenu =
			totalActions * ITEM_WIDTH + Math.max(0, totalActions - 1) * GAP;

		if (!hasOriginalMore && availableWidth >= widthForAllWithoutMenu) {
			visibleCount = totalActions;
			showMenu = false;
		} else {
			showMenu = true;
			// (k + 1) * ITEM_WIDTH + k * GAP <= availableWidth
			// k <= (availableWidth - ITEM_WIDTH) / (ITEM_WIDTH + GAP)
			const k = Math.floor((availableWidth - ITEM_WIDTH) / (ITEM_WIDTH + GAP));
			const maxVisible = hasOriginalMore ? totalActions : totalActions - 1;
			visibleCount = Math.max(0, Math.min(maxVisible, k));
		}
	}

	const visibleActions = actions.slice(0, visibleCount);
	const overflowActions = actions.slice(visibleCount);

	const overflowMenuItems: IMenuOption[] = overflowActions.map((action) => ({
		label: action.label,
		icon: action.icon,
		disabled: action.disabled,
		onClick: action.onClick,
	}));

	const allMenuOptions: IMenuOption[] = [
		...overflowMenuItems,
		...originalMenuItems,
	];

	return (
		<>
			<Box
				ref={containerRef}
				sx={{
					display: "flex",
					alignItems: "center",
					justifyContent: "flex-start",
					width: "100%",
					minWidth: 0,
					gap: `${GAP}px`,
					svg: {
						cursor: "pointer",
					},
				}}
			>
				{visibleActions.map((action) => action.render)}
				{showMenu && allMenuOptions.length > 0 && (
					<ContextMenu menuOptions={allMenuOptions} />
				)}
			</Box>
			{downloadLoading && (
				<Dialog
					open={downloadLoading || false}
					size="sm"
					onClose={() => { }}
					title=""
				>
					<Stack
						minHeight={"200px"}
						justifyContent={"center"}
						direction={"column"}
						alignItems={"center"}
					>
						<CircularProgress size={40} />
						<Typography
							variant="h3"
							marginTop={2}
						>
							Download in progress..
						</Typography>
					</Stack>
				</Dialog>
			)}

			{openDeleteModal && (
				<Dialog
					title=""
					size="sm"
					open={openDeleteModal}
					onClose={() => setOpenDeleteModal(false)}
					actions={[
						{
							type: "button",
							label: "Close",
							variant: "text",
							onClick: () => setOpenDeleteModal(false),
						},
						{
							type: "submit",
							label: "Confirm",
							variant: "contained",
							onClick: status === "INACTIVE" ? onReactiveClick : onDeleteClick,
						},
					]}
				>
					<Typography variant="h3">
						{status === "INACTIVE"
							? "Are you sure you want to reactive?"
							: "Are you sure you want to delete?"}
					</Typography>
				</Dialog>
			)}
		</>
	);
};
